import { useShowContext } from 'framework/show';
import { useCallback, useEffect, useState } from 'react';
import { DataGridLayout, RelatedList, RelationListLayout } from 'framework/list';
import { FullDateField, TextField } from 'framework/ui/field';
import { ActivityInstance } from 'domain/gpo/cockpit/instance/processInstanceService';
import VariablesLinkField from 'domain/gpo/cockpit/instance/Show/history/VariablesLinkField';
import ActivityIdField from 'domain/gpo/cockpit/instance/Show/history/ActivityIdField';
import { BpmnDiagramService, EventType } from 'domain/gpo/cockpit/facade/useBpmnJs';
import MediumHeading from 'domain/gpo/cockpit/instance/Show/MediumHeading';
import { SimpleTooltip } from 'framework/ui/ui-builder';
import Box from '@mui/material/Box';

interface Props {
  bpmnJs?: BpmnDiagramService;
}
const ProcessHistory = ({ bpmnJs }: Props) => {
  const { data } = useShowContext();
  const activities =
    data &&
    data.getValueBySource('activities')?.sort((a1: ActivityInstance, a2: ActivityInstance) => {
      let result;
      if (a1.startDateTime > a2.startDateTime) {
        result = -1;
      } else if (a1.startDateTime < a2.startDateTime) {
        result = 1;
      } else {
        result = 0;
      }
      return result;
    });

  const [highlightedItem, setHighlightedItem] = useState<any>();
  const getRowProps = useCallback(
    (activity: ActivityInstance) => {
      const isHighlighted =
        highlightedItem &&
        activity.executionId === highlightedItem.executionId &&
        activity.activityId === highlightedItem.activityId;

      return {
        sx: {
          backgroundColor: isHighlighted ? 'background.success' : undefined,
          '& td': {
            paddingTop: '10px !important',
            paddingBottom: '10px !important',
          },
        },
      };
    },
    [highlightedItem],
  );

  const isDiagramLoaded = bpmnJs?.isLoaded();

  useEffect(() => {
    if (isDiagramLoaded && activities) {
      bpmnJs?.addEventListener(e => {
        if (e.type === EventType.CLICK) {
          const activityId = e.element?.id;
          setHighlightedItem({
            activityId,
            executionId: activities.find((a: ActivityInstance) => a.activityId === activityId)?.executionId,
          });
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDiagramLoaded, activities]);

  const getDataMethod = async () => {
    return {
      totalElements: activities.length,
      totalPages: 1,
      pageNumber: 1,
      result: activities,
    };
  };

  return (
    <>
      <Box sx={{ '& > *': { display: 'inline-block' } }}>
        <MediumHeading>History</MediumHeading>
        <SimpleTooltip
          id="history-tooltip"
          isBigText
          sxIcon={{ marginLeft: '0.5em', fontSize: '1.5em', cursor: 'pointer', transform: 'translateY(3px)' }}
        >
          Use double click on a diagram element to open variables. Click on a passed activity on the diagram to
          highlight it in the table.
        </SimpleTooltip>
      </Box>
      <RelatedList getDataMethod={getDataMethod}>
        <RelationListLayout actions={[]}>
          <DataGridLayout showTotal={false} buttons={[]} isSmallTable rowProps={getRowProps}>
            <ActivityIdField
              activitySource="activityId"
              executionSource="executionId"
              bpmnJs={bpmnJs}
              setHighlightedItem={setHighlightedItem}
              label="Activity ID"
            />
            <TextField source="activityName" label="Activity name" />
            <VariablesLinkField source="executionId" label="Variables link" />
            <FullDateField source="startDateTime" label="Started at" />
            <FullDateField source="endDateTime" label="Ended at" />
          </DataGridLayout>
        </RelationListLayout>
      </RelatedList>
    </>
  );
};

export default ProcessHistory;
